import React, { useState } from 'react';
import type { ComponentType } from 'react';
import { KeyIcon, HomeIcon, RefreshCwIcon, BuildingIcon, TrendingUpIcon, UnlockIcon, MapIcon, TargetIcon, ChevronDownIcon } from 'lucide-react';
type Service = {
  title: string;
  description: string;
  icon: ComponentType<{
    className?: string;
  }>;
};
type Accent = 'accent' | 'primary';
const buyerServices: Service[] = [{
  title: 'First Home Purchase',
  description: 'We help you buy your first home with a clear plan — so you start strong and keep your options open.',
  icon: KeyIcon
}, {
  title: 'Next Home / Upgrade',
  description: 'Moving up? We structure your next purchase to keep your current property or release equity smartly.',
  icon: HomeIcon
}, {
  title: 'Refinance',
  description: "Better rate, better structure, or releasing equity — we find the right fit for where you're headed.",
  icon: RefreshCwIcon
}];
const investorServices: Service[] = [{
  title: 'First Investment',
  description: 'Your first investment property, structured properly from day one so you can keep going.',
  icon: BuildingIcon
}, {
  title: 'Scaling Your Portfolio',
  description: 'Growing beyond one property? We build the lending strategy to support it — not just the next deal.',
  icon: TrendingUpIcon
}, {
  title: 'Equity Release & Structuring',
  description: 'Unlock equity and restructure loans to fund your next move without overextending.',
  icon: UnlockIcon
}];
interface ServiceCategoryProps {
  title: string;
  headerIcon: ComponentType<{
    className?: string;
  }>;
  services: Service[];
  accent: Accent;
}
function ServiceCategory({
  title,
  headerIcon: HeaderIcon,
  services,
  accent
}: ServiceCategoryProps) {
  // Collapsible on mobile only; always expanded from lg upward.
  const [open, setOpen] = useState(false);
  const headerIconClasses = accent === 'accent' ? 'bg-accent-100 dark:bg-accent-500/15 text-accent-600 dark:text-accent-400' : 'bg-primary-100 dark:bg-primary-500/15 text-primary-600 dark:text-primary-400';
  const cardHover = accent === 'accent' ? 'hover:border-accent-300 dark:hover:border-accent-500/30' : 'hover:border-primary-300 dark:hover:border-primary-500/30';
  const iconHover = accent === 'accent' ? 'group-hover:text-accent-600 dark:group-hover:text-accent-400 group-hover:bg-accent-50 dark:group-hover:bg-accent-500/10 group-hover:border-accent-200 dark:group-hover:border-accent-500/20' : 'group-hover:text-primary-600 dark:group-hover:text-primary-400 group-hover:bg-primary-50 dark:group-hover:bg-primary-500/10 group-hover:border-primary-200 dark:group-hover:border-primary-500/20';
  return <div data-id="element-463" data-mp="olbs6zkjyywn">
      {/* Header — button (toggles) on mobile, static from lg */}
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="w-full flex items-center gap-3 mb-4 lg:mb-8 pb-4 border-b border-slate-200 dark:border-white/10 text-left lg:cursor-default lg:pointer-events-none" data-id="element-464" data-mp="onegnf624jz9">
        <div className={`p-2.5 rounded-xl ${headerIconClasses}`} data-id="element-465" data-mp="293w0yen6ku3i">
          <HeaderIcon className="w-5 h-5" data-id="element-466" data-mp="24bcdmpyoykli" />
        </div>
        <h3 className="text-xl font-bold text-slate-900 dark:text-white" data-id="element-467" data-mp="1zdavc793apb3">
          {title}
        </h3>
        <ChevronDownIcon className={`w-5 h-5 ml-auto text-slate-400 dark:text-slate-500 transition-transform lg:hidden ${open ? 'rotate-180' : ''}`} data-id="element-468" data-mp="1ue817hune3hw" />
      </button>

      {/* Service list — collapsible on mobile, always shown from lg */}
      <div className={`space-y-4 ${open ? 'block' : 'hidden'} lg:block`} data-id="element-469" data-mp="1le1pa0ellbku">
        {services.map((service, index) => {
        const Icon = service.icon;
        return <div key={index} className={`group p-5 rounded-xl bg-white dark:bg-[#1a2035] border border-slate-200 dark:border-white/5 ${cardHover} transition-all duration-200 shadow-sm hover:shadow-md dark:shadow-none`} data-id="element-470" data-mp="3fqhvj17e8ppp" data-mpi={index}>
              <div className="flex gap-4" data-id="element-471" data-mp="2cgn4ir4eds0w">
                <div className="flex-shrink-0 mt-0.5" data-id="element-472" data-mp="2cfc4n2e2p4f2">
                  <div className={`p-2 rounded-lg bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 text-slate-400 dark:text-slate-500 ${iconHover} transition-colors`} data-id="element-473" data-mp="1yguhp6vzrvy7">
                    <Icon className="w-5 h-5" data-id="element-474" data-mp="1opzwm5uhf2ec" />
                  </div>
                </div>
                <div data-id="element-475" data-mp="1ti147egpg4y1">
                  <h4 className="text-base font-semibold text-slate-900 dark:text-white mb-1.5" data-id="element-476" data-mp="1tiklqu2g0rbj">
                    {service.title}
                  </h4>
                  <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed" data-id="element-477" data-mp="1kp7l15y50lew">
                    {service.description}
                  </p>
                </div>
              </div>
            </div>;
      })}
      </div>
    </div>;
}
export function ServicesSection() {
  return <section id="services" className="py-24 bg-[#f8f9fb] dark:bg-[#0f1629] transition-colors duration-300 relative z-10" data-id="element-478" data-mp="r4yelrnfi2zp">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-479" data-mp="n5h8cw06h1th">
        <div className="text-center mb-16" data-id="element-480" data-mp="113xofdjxk4oa">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none mb-6" data-id="element-481" data-mp="114z11mglgz84">
            <span className="text-sm font-medium text-primary-600 dark:text-primary-400" data-id="element-482" data-mp="1a2stunf5haqj">
              What We Help With
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-483" data-mp="1jtvsliklxdmn">
            Strategy Built Around You
          </h2>
          <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto" data-id="element-484" data-mp="1evlyzcej6l35">
            We don't just help you get a loan — we help you build a strategy
            around it. Every move is designed to set you up for the next one.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16" data-id="element-485" data-mp="1vx813j2e18az">
          <ServiceCategory title="For Home Buyers" headerIcon={MapIcon} services={buyerServices} accent="accent" data-id="element-486" data-mp="20v9r6ny8e7vy" />
          <ServiceCategory title="For Investors" headerIcon={TargetIcon} services={investorServices} accent="primary" data-id="element-487" data-mp="25sjhe5tg2nku" />
        </div>
      </div>
    </section>;
}